import React, { useState } from "react";
import { fileUrl } from "../utils/api";
import SocialIcons from "./SocialIcons";

/**
 * Horizontal row layout: photo on the left, name/role/bio/social on the right.
 * Each researcher takes up one full row; rows stack vertically.
 */
export const TeamCard = ({ member, index = 0, badgeLabel = "Researcher" }) => {
  const gradYear = String(member.grad_year || "").trim();
  const [broken, setBroken] = useState(false);
  const src = member.image_path ? fileUrl(member.image_path) : null;
  const orderLabel = String(index + 1).padStart(2, "0");

  return (
    <article
      // Anchor target for /team#member-<id>, linked to from project credits.
      // scroll-mt clears the sticky navbar so the card isn't tucked under it.
      id={`member-${member.id}`}
      className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-0 border hairline bg-[#F9F8F6] fade-up scroll-mt-28 card-lift"
      style={{ animationDelay: `${Math.min(index, 8) * 80}ms` }}
      data-testid={`team-card-${member.id}`}
    >
      {/* Photo.
          Stacked (mobile), the portrait is a 4:5 box — the card is one column,
          so nothing else governs its height.

          Side by side, it fills the row instead. It used to hold that same fixed
          4:5 box inside a cell that stretched, so any bio taller than the
          portrait left the stone mat showing underneath as a grey slab — and
          browser zoom made it worse, because zooming grows the text column while
          the portrait stayed the same shape. Filling the row removes the gap at
          every bio length and every zoom level.

          The trade this reverses is real but much the lesser one: rows of
          different heights crop their portraits slightly differently. `min-h`
          stops a short bio from squashing a portrait into a letterbox, and the
          25% vertical bias keeps heads in the upper third at any height. */}
      <div className="bg-[#F9F8F6]">
        <div className="relative w-full overflow-hidden media-zoom bg-[#E6E4DD] aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[400px] lg:max-h-[620px]">
          {src && !broken ? (
            <img
              src={src}
              alt={member.name}
              onError={() => setBroken(true)}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-[center_25%] grayscale-[18%]"
            />
          ) : (
            // No photograph on file — the group's own mark stands in, rather
            // than a generic person glyph. Held back to 55% so a card without a
            // portrait reads as "not supplied" and never competes with the real
            // ones beside it. aria-hidden: the member's name is already the
            // heading right next to it.
            <div className="absolute inset-0 flex items-center justify-center bg-[#E6E4DD]">
              <img
                src="/iwm-logo-128.png"
                srcSet="/iwm-logo-128.png 1x, /iwm-logo-512.png 2x"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="w-1/2 max-w-[120px] opacity-55"
              />
            </div>
          )}
          <div className="absolute top-3 left-3 font-mono text-[10px] tracking-widest uppercase text-[#F9F8F6] bg-[#1C2722]/80 px-2 py-1">
            {orderLabel} · {badgeLabel}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8 lg:p-10 flex flex-col border-t lg:border-t-0 lg:border-l hairline">
        <div className="overline" data-testid={`team-role-${member.id}`}>
          {member.role}
          {gradYear && (
            <>
              <span className="mx-2 text-[#A17E3B]">·</span>
              <span data-testid={`team-gradyear-${member.id}`}>{gradYear}</span>
            </>
          )}
        </div>
        <h3
          className="font-serif text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-[#1C2722] mt-2"
          data-testid={`team-name-${member.id}`}
        >
          {member.name}
        </h3>

        {member.bio && (
          // Left-aligned (ragged right). Justifying this was tried and dropped:
          // at this column width it needs hyphenation to avoid rivers of white
          // space, and the broken words read worse than the ragged edge does.
          // max-w-4xl rather than 2xl so the bio fills the column instead of
          // stopping well short of it.
          <p
            className="mt-5 text-[14.5px] leading-relaxed text-[#4A5A52] max-w-4xl"
            data-testid={`team-bio-${member.id}`}
          >
            {member.bio}
          </p>
        )}

        <div className="mt-auto pt-8">
          <SocialIcons member={member} testIdPrefix="team-social" />
        </div>
      </div>
    </article>
  );
};

export default TeamCard;
